import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHero from '../components/PageHero';
import ServiceFlipCard from '../components/ServiceFlipCard';
import FeatureList from '../components/FeatureList';
import { customSoftwareContent } from '../data/customSoftware';
import { FaGlobe, FaSearch, FaRobot, FaLaptopCode } from 'react-icons/fa';

const sectionHeadingStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  letterSpacing: '0.05em',
};

const paths = [
  {
    id: 'website',
    label: 'I need a website that brings leads',
    hint: 'Trust + forms that convert',
  },
  {
    id: 'seo',
    label: 'I need more local customers',
    hint: 'Show up when they search',
  },
  {
    id: 'ai-automations',
    label: 'I need follow ups on autopilot',
    hint: 'Never lose a warm lead',
  },
  {
    id: 'custom-software',
    label: 'I need custom software for my business',
    hint: 'Built around how you operate',
  },
];

const services = [
  {
    id: 'website',
    icon: FaGlobe,
    title: 'Website',
    tagline: 'Turn visitors into leads',
    price: 'Starting at $500',
    outcome: 'A site that looks credible and asks for the sale.',
    bestFor: 'Best if people land on your page and leave without contacting you.',
    description:
      'Custom coded websites built on marketing psychology. Every element is designed to build trust, establish credibility, and convert visitors into leads through strategic forms and calls to action.',
    highlights: ['Looks pro on phone', 'Clear call to action', 'Forms that capture leads'],
    packages: [
      {
        name: 'Website package',
        chooseIf: 'Choose this if you already have a domain and Google Business Profile.',
        badge: null,
        features: [
          'SEO (Search Engine Optimization)',
          'Leave a rating on Google and Yelp',
          'Professional website design and development',
          'Responsive design (mobile friendly)',
          'Contact forms and lead capture',
          'Social media integration',
          'Google Analytics setup',
          'Google Business Profile optimization',
          'Content management system (CMS)',
          '1 month of maintenance and support',
        ],
      },
      {
        name: 'Website package with domain and hosting',
        chooseIf: 'Choose this if you are starting from scratch. We set up domain, hosting, and Google Business Profile.',
        badge: 'Most popular for new businesses',
        features: [
          'Domain and hosting setup (first year)',
          'Google Business Profile setup and optimization',
          'SEO (Search Engine Optimization)',
          'Leave a rating on Google and Yelp',
          'Professional website design and development',
          'Responsive design (mobile friendly)',
          'Contact forms and lead capture',
          'Social media integration',
          'Google Analytics setup',
          'Content management system (CMS)',
          '1 month of maintenance and support',
        ],
      },
    ],
    addOns: [
      {
        name: 'Monthly maintenance',
        note: 'Optional. We keep your site updated, fast, and working so you do not have to.',
        price: 'Starting at $100/mo',
      },
    ],
  },
  {
    id: 'seo',
    icon: FaSearch,
    title: 'SEO',
    tagline: 'Get found by people ready to buy',
    price: 'Starting at $200/mo',
    outcome: 'Your business shows up when locals search for what you offer.',
    bestFor: 'Best if customers say “I found you on Google” rarely or never.',
    description:
      'Rank higher on Google and show up locally. We optimize your site, Google Business Profile, and content so the right customers find you.',
    highlights: ['Local search visibility', 'Google Business Profile', 'Keywords that matter'],
    features: [
      'On page SEO optimization',
      'Local search visibility',
      'Google Business Profile setup and optimization',
      'Keyword targeting for your area',
      'Search analytics and performance tracking',
      'Content recommendations that convert',
    ],
  },
  {
    id: 'ai-automations',
    icon: FaRobot,
    title: 'AI Automations',
    tagline: 'Follow up while you sleep',
    price: null,
    outcome: 'Every lead gets a fast, personal reply without you typing every message.',
    bestFor: 'Best if leads go cold because follow up takes too long.',
    description:
      'Smart workflows that follow up with leads, route inquiries, and handle repetitive tasks so you can focus on running your business.',
    highlights: ['Instant follow ups', 'Lead nurturing', 'Less busywork'],
    features: [
      'Automated lead follow ups',
      'Inquiry routing and notifications',
      'Lead nurturing sequences',
      'Repetitive task automation',
      'Custom workflows for your business',
    ],
    packages: [
      {
        name: 'Email Outreach and Lead Nurturing Systems',
        chooseIf: 'Choose this if you want email campaigns and sequences running without manual work.',
        badge: 'Flat rate setup',
        price: 'Starting at $500 flat rate',
        note: 'Additional monthly only if you want ongoing maintenance.',
        features: [
          'Personalized email campaigns',
          'Automated sequences and follow ups',
          'Lead nurturing workflows',
          'Optional monthly maintenance',
        ],
      },
    ],
  },
  {
    id: 'custom-software',
    icon: FaLaptopCode,
    title: 'Customized Software',
    ...customSoftwareContent,
    seeMoreLink: '/custom-software',
  },
];

function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

function ServiceSection({ service, onBook, isActive }) {
  const [ref, visible] = useScrollReveal(0.12);

  return (
    <section
      ref={ref}
      id={service.id}
      className={`mb-12 md:mb-16 scroll-mt-28 transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="flex justify-center mb-8">
        <ServiceFlipCard
          icon={service.icon}
          title={service.title}
          tagline={service.tagline}
          price={service.price}
          outcome={service.outcome}
          description={service.description}
          bestFor={service.bestFor}
          highlights={service.highlights}
          seeMoreLink={service.seeMoreLink}
          isActive={isActive}
          className="max-w-md"
        />
      </div>

      <div className="max-w-5xl mx-auto">
        {service.features && (
          <div className="bg-white/12 backdrop-blur-sm rounded-2xl border border-white/25 shadow-md p-6 md:p-8 mb-8 max-w-3xl mx-auto transition-transform duration-300 hover:-translate-y-0.5">
            <FeatureList features={service.features} />
            <div className="flex flex-wrap items-center gap-4 mt-6">
              {service.seeMoreLink && (
                <Link to={service.seeMoreLink} className="group">
                  <button
                    type="button"
                    className="w-full max-w-[260px] py-3 font-medium rounded-full border-2 border-[#ff7a59] bg-white/40 backdrop-blur-sm text-gray-700 transition-all duration-300 shadow-md transform hover:scale-105 hover:shadow-lg"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    View More
                  </button>
                </Link>
              )}
              <button
                type="button"
                onClick={onBook}
                className="group w-full max-w-[260px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#ff7a59' }}
              >
                <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  FREE CONSULTATION!
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </button>
            </div>
          </div>
        )}

        {service.packages && (
          <div
            className={`mb-8 ${
              service.packages.length > 1
                ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
                : 'max-w-3xl mx-auto'
            }`}
          >
            {service.packages.map((pkg, index) => (
              <div
                key={pkg.name}
                className={`relative bg-white/12 backdrop-blur-sm rounded-2xl border shadow-md p-6 md:p-8 flex flex-col h-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${
                  pkg.badge ? 'border-[#ff7a59]/35' : 'border-white/25'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {pkg.badge && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-semibold px-3 py-1 rounded-full text-white whitespace-nowrap"
                    style={{ fontFamily: "'Poppins', sans-serif", backgroundColor: '#ff7a59' }}
                  >
                    {pkg.badge}
                  </span>
                )}
                <h3
                  className="text-lg md:text-xl font-bold mb-2 text-gray-700 text-center"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {pkg.name}
                </h3>
                {pkg.price && (
                  <p
                    className="text-sm font-semibold mb-2 text-center"
                    style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59' }}
                  >
                    {pkg.price}
                  </p>
                )}
                <p
                  className="text-xs md:text-sm mb-5 text-center rounded-xl bg-white/25 px-3 py-2 border border-white/30"
                  style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
                >
                  {pkg.chooseIf || pkg.note}
                </p>
                {pkg.note && pkg.chooseIf && (
                  <p
                    className="text-xs italic mb-4 text-center"
                    style={{ fontFamily: "'Poppins', sans-serif", color: '#292929', opacity: 0.75 }}
                  >
                    {pkg.note}
                  </p>
                )}
                <div className="flex-1 mb-6">
                  <FeatureList features={pkg.features} />
                </div>
                <button
                  type="button"
                  onClick={onBook}
                  className="group w-full max-w-[260px] mx-auto py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
                  style={{ backgroundColor: '#ff7a59' }}
                >
                  <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    FREE CONSULTATION!
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </button>
              </div>
            ))}
          </div>
        )}

        {service.addOns?.map((addOn) => (
          <div
            key={addOn.name}
            className="bg-white/12 backdrop-blur-sm rounded-2xl border border-white/25 shadow-md p-6 md:p-8 mb-4 max-w-3xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-center md:text-left transition-transform duration-300 hover:-translate-y-0.5"
          >
            <div>
              <h3
                className="text-base md:text-lg font-bold text-gray-700 mb-1"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {addOn.name}
              </h3>
              {addOn.price && (
                <p
                  className="text-sm font-semibold mb-1"
                  style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59' }}
                >
                  {addOn.price}
                </p>
              )}
              <p
                className="text-xs md:text-sm"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929', opacity: 0.8 }}
              >
                {addOn.note}
              </p>
            </div>
            <button
              type="button"
              onClick={onBook}
              className="group w-full max-w-[240px] mx-auto md:mx-0 py-2.5 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl flex-shrink-0"
              style={{ backgroundColor: '#ff7a59' }}
            >
              <span className="relative z-10 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                FREE CONSULTATION!
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Services() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePath, setActivePath] = useState(null);

  const handleConsultationClick = () => {
    navigate('/book');
  };

  const scrollToService = (id) => {
    setActivePath(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (!activePath) return undefined;
    const timer = setTimeout(() => setActivePath(null), 2500);
    return () => clearTimeout(timer);
  }, [activePath]);

  useEffect(() => {
    if (!location.hash) return undefined;

    const id = location.hash.replace('#', '');
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActivePath(id);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  return (
    <PageLayout>
      <PageHero
        eyebrow="What we offer"
        title="Services"
        subtitle="Pick what you need. We build the system that brings customers in and helps you close them."
        className="pb-8 md:pb-10"
        maxWidth="max-w-6xl"
      />

      <section className="px-4 md:px-8 pb-20">
        <div className="max-w-6xl mx-auto">

          <div className="mb-14 max-w-3xl mx-auto animate-fade-in-up animate-delay-400">
            <p
              className="text-center text-sm font-semibold mb-4 uppercase tracking-wider"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59' }}
            >
              Not sure where to start? Tap one.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {paths.map((path) => (
                <button
                  key={path.id}
                  type="button"
                  onClick={() => scrollToService(path.id)}
                  className={`text-left rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                    activePath === path.id
                      ? 'bg-[#ff7a59] border-[#ff7a59] text-white shadow-lg scale-[1.02]'
                      : 'bg-white/30 border-white/50 hover:border-[#ff7a59]/50'
                  }`}
                >
                  <span
                    className="block text-sm font-semibold mb-1"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      color: activePath === path.id ? '#fff' : '#292929',
                    }}
                  >
                    {path.label}
                  </span>
                  <span
                    className="block text-xs"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      color: activePath === path.id ? 'rgba(255,255,255,0.9)' : '#292929',
                      opacity: activePath === path.id ? 1 : 0.7,
                    }}
                  >
                    {path.hint}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {services.map((service) => (
            <ServiceSection
              key={service.id}
              service={service}
              onBook={handleConsultationClick}
              isActive={activePath === service.id}
            />
          ))}

          <div className="py-10 text-center max-w-xl mx-auto">
            <p
              className="text-base md:text-lg mb-5 text-gray-700"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Still unsure? Book a free consult. We will tell you exactly what you need (and what you do not).
            </p>
            <button
              type="button"
              onClick={handleConsultationClick}
              className="group w-[200px] md:w-[220px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
              style={{ backgroundColor: '#ff7a59' }}
            >
              <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                FREE CONSULTATION!
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
